import { useState, useMemo } from 'react';
import { useOrganizations } from '@/domains/accounts/services/accounts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Loader2, User, Search } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';
import { Address } from '@/shared/types/addressTypes';
import { useAddAddressToOrganization } from '@/domains/addresses/services/addresses';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Organization } from '@/shared/types/userTypes';
import SearchBar from '@/shared/components/SearchBar';

interface AddAddressToAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  address: Address | undefined;
  organizationsWithAddress: Organization[] | undefined;
  selectedRegionCode: string;
}

export default function AddAddressToAccountDialog({
  open,
  onOpenChange,
  onSuccess,
  address,
  organizationsWithAddress,
  selectedRegionCode,
}: AddAddressToAccountDialogProps) {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState("");
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations(open, selectedRegionCode);
  const addAddressToOrganization = useAddAddressToOrganization(address?.addressReferenceId || '');

  // Filter out accounts that are already associated with the address
  const availableOrganizations = useMemo(() => organizations?.filter(organization =>
    !(organizationsWithAddress ?? []).some(o => o.organizationId === organization.organizationId)
  ) || [], [organizations, organizationsWithAddress]);

  // Filter accounts based on search term
  const filteredOrganizations = useMemo(() => {
    return availableOrganizations.filter((organization) =>
      organization.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.organizationId.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [availableOrganizations, searchTerm]);

  const handleSubmit = async () => {
    if (!selectedOrganizationId) {
      showError(ERROR.ADDRESS.SELECT_ACCOUNT);
      return;
    }

    try {
      await addAddressToOrganization.mutateAsync(selectedOrganizationId)
      showSuccess(SUCCESS.ADDRESS.ADDED_TO_ACCOUNT);
      onSuccess();
    } catch (error) {
      showErrorWithApiError(error, ERROR.ADDRESS.ADD_TO_ACCOUNT);
    } finally {
      handleDialogClose(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setSearchTerm("");
      setSelectedOrganizationId('');
    }
    onOpenChange(open);
  };

  const showSearch = availableOrganizations.length > 0;

  if (!address) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Address to Organizations</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4">
            <h4 className="font-medium mb-2">Address Details</h4>
            <p className="text-sm">
              {address.formattedAddress}
            </p>
          </div>

          <div className="space-y-4">
            {/* Search Input - only show when there are accounts */}
            {showSearch && (
              <div className="relative">
                <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search accounts..." />
              </div>
            )}

            <h4 className="font-medium">Select Accounts</h4>
            {isLoadingOrganizations ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : filteredOrganizations.length === 0 ? (
              searchTerm ? (
                <EmptyView
                  icon={Search}
                  size="small"
                  title="No Accounts Found"
                  description={`No accounts match "${searchTerm}". Try adjusting your search.`}
                />
              ) : (
                <EmptyView
                  icon={User}
                  size="small"
                  title="No Accounts Available"
                  description="No available accounts to add this address to. All accounts already have this address."
                />
              )
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                <RadioGroup
                  value={selectedOrganizationId}
                  onValueChange={(value) => {
                    const organization = filteredOrganizations.find(o => o.organizationId === value);
                    setSelectedOrganizationId(organization?.organizationId || '');
                  }}
                  className="space-y-2"
                >
                  {filteredOrganizations.map((organization) => (
                    <div key={organization.organizationId} className="flex items-center space-x-2">
                      <RadioGroupItem value={organization.organizationId} id={`organization-${organization.organizationId}`} />
                      <Label
                        htmlFor={`organization-${organization.organizationId}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        <div className="flex flex-col">
                          <span>{organization.name}</span>
                          <span className="text-xs">ID: {organization.organizationId}</span>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => handleDialogClose(false)}
            disabled={addAddressToOrganization.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addAddressToOrganization.isPending || availableOrganizations.length === 0 || !selectedOrganizationId}
            className="gap-2">
            {addAddressToOrganization.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              'Add to Selected Account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 